import {
	ListItemIcon,
	CSSObject,
	Toolbar,
	Button,
	styled,
	Drawer,
	Theme,
	alpha,
	List,
	css,
	darken,
} from '@mui/material';
import {NavLink} from 'react-router-dom';
import {StyledListItemProps, StyledToolbarProps} from './types';

const drawerWidth = '288px';

const openedMixin = (theme: Theme): CSSObject => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `${theme.spacing(8)}`,
	[theme.breakpoints.up('sm')]: {
		width: `${theme.spacing(10)}`,
	},
});

export const StyledDrawer = styled(Drawer)`
	box-sizing: border-box;
	width: ${drawerWidth};
	white-space: nowrap;
	flex-shrink: 0;

	& .MuiDrawer-paper {
		border-right: 0;
		box-shadow:
			0px 1px 8px 0px rgba(0, 0, 0, 0.12),
			0px 3px 4px 0px rgba(0, 0, 0, 0.14),
			0px 3px 3px -2px rgba(0, 0, 0, 0.2);
	}

	${({theme, open}) =>
		open
			? {
					...openedMixin(theme),
					'& .MuiDrawer-paper': openedMixin(theme),
				}
			: {
					...closedMixin(theme),
					'& .MuiDrawer-paper': closedMixin(theme),
				}}
`;

export const StyledToolbar = styled(Toolbar)<StyledToolbarProps>`
	position: relative;
	padding: ${({open}) => (open ? '4px 32px' : '16px')};
`;

const BaseToggleButton = styled(Button)`
	background-color: ${({theme}) =>
		theme.palette.other.planColor || theme.colors.light.primary.main};

	&:hover {
		background-color: ${({theme}) =>
			theme.name === 'hap' || !theme.palette.other.planColor
				? theme.colors.light.primary.dark
				: darken(theme.palette.other.planColor || '', 0.2)} !important;
	}
`;

export const CloseButton = styled(BaseToggleButton)`
	position: absolute;
	right: 0;
	top: 50%;
	transform: translateY(-50%);
	border-radius: 24px 0 0 24px;
	width: 48px;
	height: 32px;
	min-width: inherit;
`;

export const OpenButton = styled(BaseToggleButton)`
	border-radius: 0;
	box-shadow: none;

	&:hover {
		box-shadow: none;
	}
`;

export const StyledList = styled(List)<StyledListItemProps>`
	padding: ${({open}) => (open ? '32px 0' : '0')};
`;

export const StyledNavLink = styled(NavLink)<StyledListItemProps>`
	border-radius: ${({open}) => (open ? '8px' : 'inherit')};
	padding: 20px 16px;
	justify-content: center;
	display: flex;

	&:is(:focus, :active, :hover),
	&.active {
		background-color: ${({theme}) => alpha(theme.palette.primary.main, 0.08)};

		.MuiListItemIcon-root {
			color: ${({theme}) => theme.palette.primary.main};
		}

		.MuiListItemText-root .MuiTypography-root {
			color: ${({theme}) => theme.palette.primary.dark};
		}
	}

	& .MuiTouchRipple-root .MuiTouchRipple-child {
		background-color: ${({theme}) => theme.palette.primary.main};
	}
`;

export const StyledListItemIcon = styled(ListItemIcon)<StyledListItemProps>`
	min-width: ${({open}) => (open ? '56px' : 'inherit')};
`;

export const StyledModalButton = styled('div')<StyledListItemProps>`
	border-radius: ${({open}) => (open ? '8px' : 'inherit')};
	padding: 20px 16px;
	justify-content: center;
	display: flex;

	${({$isBooking}) =>
		$isBooking &&
		css`
			background-color: ${({theme}) => alpha(theme.palette.primary.main, 0.08)};

			.MuiListItemIcon-root {
				color: ${({theme}) => theme.palette.primary.main};
			}

			.MuiListItemText-root .MuiTypography-root {
				color: ${({theme}) => theme.palette.primary.dark};
			}
		`};

	&:is(:hover) {
		background-color: ${({theme}) => alpha(theme.palette.primary.main, 0.08)};

		.MuiListItemIcon-root {
			color: ${({theme}) => theme.palette.primary.main};
		}

		.MuiListItemText-root .MuiTypography-root {
			color: ${({theme}) => theme.palette.primary.dark};
		}
	}

	& .MuiTouchRipple-root .MuiTouchRipple-child {
		background-color: ${({theme}) => theme.palette.primary.main};
	}
`;
